/**
 * AthleteMePage — the paying athlete's private production + earnings
 * dashboard. Route: /athlete/me and /athlete/dashboard.
 *
 * "Hope Energy"-inspired dark liquid-glass aesthetic: full-bleed hero
 * portrait, floating glass cards, big milestone pipeline, tabs for
 * Earnings / Messages / Payouts.
 */
import { useEffect, useMemo, useState, useRef, useId } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, Award, Sparkles, Copy, ExternalLink, Film,
  Calendar, CheckCircle2, Circle, ArrowRight, Trophy,
  Handshake, BarChart3, Loader2, User, Camera, Bell,
} from 'lucide-react';
import AgentInviteBanner from '../components/athlete/AgentInviteBanner';
import MediaHubWidgetPromoCard from '../components/athlete/MediaHubWidgetPromoCard';
import BusinessInquiriesTile from '../components/athlete/BusinessInquiriesTile';
import AthleteMessagesInbox from '../components/athlete/AthleteMessagesInbox';
// iter369 · Payouts removed pre-launch — imports pruned to silence
// unused-vars. Re-add when we bring Stripe Connect payouts back.
// import ConnectPayoutSetupCard from '../components/connect/ConnectPayoutSetupCard';
// import ConnectTransactionList from '../components/connect/ConnectTransactionList';
import { useUserAuth } from '../contexts/UserAuthContext';
import { FoundingContactButton } from '../components/founding/FoundingAthlete';
import { setMediaHubWidgetAthlete } from '../lib/nativeBridge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (c) => (c == null ? '$0' : `$${(c / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const resolveSrc = (u) => (!u ? '' : (u.startsWith('http') || u.startsWith('data:') || u.startsWith('blob:') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

const PRODUCTION_STAGES = [
  { id: 'intake',            label: 'Intake',      caption: 'Contract & kickoff' },
  { id: 'video_shoot',       label: 'Video',       caption: 'On-set filming' },
  { id: 'photo_shoot',       label: 'Photo',       caption: 'Studio + on-location' },
  { id: 'editing',           label: 'Editing',     caption: 'Cut, color, sound' },
  { id: 'brand_integration', label: 'Brand',       caption: 'Partner placements' },
  { id: 'delivered',         label: 'Delivered',   caption: 'Live on Flythouse' },
];

const SOURCE_LABELS = {
  share_affiliate: 'Share Affiliate',
  subscription_rev_share: 'Subscription',
  brand_deal: 'Brand Deal',
  sponsorship: 'Sponsorship',
  merch: 'Merch',
  other: 'Other',
};

// Messages tab intentionally omitted here — the middle bottom-nav
// icon already opens /athlete/messages. Having both routes felt
// duplicative and confused users.
// iter369 · "Payouts" tab removed pre-launch — Jay cut payouts from
// the launch scope. Re-add `{ id: 'payouts', label: 'Payouts' }` when
// we bring Stripe Connect Express back.
// iter357 · "Earnings" tab also removed — payouts + earnings ledger
// are both offline pre-launch, so the tab always showed the "No
// earnings recorded yet" empty state which was demoralising +
// confusing. Bring back with `{ id: 'earnings', label: 'Earnings' }`
// once payouts ship.
const TABS = [
  { id: 'overview',  label: 'Overview' },
  // iter229o · Nudge history — every agent outreach the athlete has
  // received, so they can see who nudged them and when.
  { id: 'notifications', label: 'Notifications' },
];

const AthleteMePage = ({ embedded = false } = {}) => {
  const { setLoginOpen } = useUserAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [copied, setCopied] = useState(false);
  // iter229o · Nudge history state — lazy-loaded when the tab is opened.
  const [nudges, setNudges] = useState(null);

  useEffect(() => {
    if (!embedded) document.title = 'Your Dashboard · Flythouse';
    axios.get(`${API}/me/athlete-dashboard-v2`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Sign in to view your dashboard.'));
    return () => { if (!embedded) document.title = 'Flythouse'; };
  }, [embedded]);

  // iter · Ties the Media Hub Home-Screen widget to THIS account —
  // zero-config, no "type your slug into Edit Widget" step. Fires
  // every time the athlete's own dashboard data loads (native-only,
  // no-ops on web). See MediaHubWidgetPromoCard.jsx / nativeBridge.js.
  useEffect(() => {
    if (data?.athlete?.slug) setMediaHubWidgetAthlete(data.athlete.slug);
  }, [data?.athlete?.slug]);

  useEffect(() => {
    if (tab !== 'notifications' || nudges !== null) return;
    axios.get(`${API}/me/nudges/history`, { withCredentials: true })
      .then((r) => setNudges(r.data?.nudges || []))
      .catch(() => setNudges([]));
  }, [tab, nudges]);

  const copy = async () => {
    if (!data?.share_link) return;
    try {
      await navigator.clipboard.writeText(data.share_link);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
      toast.success('Share link copied');
    } catch { toast.error('Copy failed'); }
  };

  if (error) {
    return (
      <div className={`${embedded ? '' : 'min-h-screen'} bg-black text-white flex items-center justify-center px-6 ${embedded ? 'py-8' : ''}`}>
        <div className="max-w-md text-center">
          <p className="text-sm uppercase tracking-[0.3em] text-white/40 font-bold mb-3">Athlete Dashboard</p>
          <p className="text-lg text-gray-400 mb-6">{error}</p>
          <button type="button" onClick={() => setLoginOpen(true)} data-testid="athlete-me-login-cta" className="inline-block px-6 py-3 rounded-full bg-white text-black font-bold text-sm hover:bg-cyan-300 transition">Sign in</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className={`${embedded ? 'py-10' : 'min-h-screen'} bg-black text-white flex items-center justify-center`}>Loading your dashboard…</div>;

  const { athlete = {}, earnings = {}, premium_package: pkg, membership, share_link, agent, onboarding = {}, production = {}, payouts = [] } = data;
  const heroSrc = resolveSrc(athlete.hero_image || athlete.photo);
  const photoSrc = resolveSrc(athlete.photo);
  const accent = agent?.accent_color || '#22D3EE';

  const currentStageIdx = production.stage ? PRODUCTION_STAGES.findIndex(s => s.id === production.stage) : -1;
  const pkgStatus = pkg?.status || null;
  const doneItems = onboarding.done || 0;
  const totalItems = onboarding.total || 6;
  const onboardingPct = totalItems > 0 ? Math.min(100, Math.round((doneItems / totalItems) * 100)) : 0;

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-black text-white ${embedded ? '' : 'pb-32'}`} data-testid="athlete-me-page">
      {/* iter165 · New-message inbox chip — surfaces unread threads
          from an already-approved agent above the pending-invite
          banner.  Auto-polls every 20s so the athlete sees agent
          messages the moment they arrive. */}
      <AthleteMessagesInbox />
      {/* iter158 · Pending agent-invite banner — shown at the very top
          of the athlete's own dashboard.  Athlete MUST approve before
          an agent can attach them to a roster. */}
      <AgentInviteBanner />
      {/* iter · Discoverable (not interrupt-popup) promo for the
          Media Hub Home Screen widget — iOS-only, self-hides once
          dismissed. See MediaHubWidgetPromoCard.jsx for why this is a
          walkthrough card, not a one-tap "add" button. */}
      {!embedded && (
        <div className="px-4 pt-3">
          <MediaHubWidgetPromoCard />
        </div>
      )}
      {/* Hero — hidden when embedded inside the /profile Earnings tab
          (the profile card above the tabs already carries the identity). */}
      {!embedded && (
      <div className="relative overflow-hidden">
        {heroSrc && (
          <div
            className="absolute inset-0 opacity-40"
            style={{ backgroundImage: `url(${heroSrc})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(28px) saturate(1.2)' }}
          />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.85) 60%, #000 100%)' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-8 pt-10 sm:pt-16 pb-8">
          <div className="flex items-end gap-4 sm:gap-6">
            <div className="relative">
              {photoSrc ? (
                <img src={photoSrc} alt="" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover ring-4" style={{ ringColor: accent, boxShadow: `0 0 60px ${accent}55` }} />
              ) : (
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-cyan-500 flex items-center justify-center text-4xl font-bold ring-4 ring-white/20">
                  {(athlete.name || '?').charAt(0)}
                </div>
              )}
              {pkgStatus === 'delivered' && (
                <span className="absolute -bottom-1 -right-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-black">Live</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.35em] font-bold" style={{ color: accent }}>Your Dashboard</p>
              <h1 className="font-display text-4xl sm:text-6xl uppercase tracking-tight text-white leading-[0.9] mt-1 truncate">{athlete.name || 'Athlete'}</h1>
              <p className="text-sm text-white/60 mt-1">{[athlete.sport, athlete.team].filter(Boolean).join(' · ')}</p>
              {agent && (
                <p className="text-[11px] uppercase tracking-widest text-white/50 mt-2">
                  Represented by <span className="text-white font-bold">{agent.agency_name}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        {/* iter357 · Earnings tiles removed while payouts are offline —
            the numbers were always zero and misled athletes. Replaced by
            a smaller Membership + Onboarding row so the dashboard still
            surfaces status at a glance. */}
        <div className="grid grid-cols-2 gap-3 -mt-4 sm:-mt-6 mb-8">
          <PubStat testid="athlete-stat-onboarding" label="Onboarding" value={`${doneItems}/${totalItems}`} icon={CheckCircle2} accent="text-cyan-400" />
          <PubStat testid="athlete-stat-member"   label="Membership"        value={membership?.status === 'active' ? 'Active' : (membership?.status || 'Not started')} icon={Sparkles} accent={membership?.status === 'active' ? 'text-emerald-400' : 'text-gray-500'} sub={membership?.status === 'active' ? `Next: ${fmtDate(membership.next_charge_at)}` : ''} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-white/10 overflow-x-auto no-scrollbar">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`athlete-tab-${t.id}`}
              className={`relative px-4 py-3 text-xs uppercase tracking-widest font-bold whitespace-nowrap transition ${tab === t.id ? 'text-white' : 'text-white/40 hover:text-white/70'}`}
            >
              {t.label}
              {tab === t.id && (
                <span className="absolute inset-x-0 -bottom-px h-[2px]" style={{ background: accent }} />
              )}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            {/* Onboarding progress */}
            <GlassCard testid="athlete-onboarding-card" title="Onboarding Progress" icon={CheckCircle2} accent="text-cyan-400">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full transition-all duration-500" style={{ width: `${onboardingPct}%`, background: accent }} />
                </div>
                <span className="text-sm font-mono text-white/70">{doneItems}/{totalItems}</span>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3 font-bold">Current Stage · {(onboarding.stage || 'invited').replace(/_/g, ' ')}</p>
              <ul className="space-y-2">
                {(onboarding.checklist || []).map(item => (
                  <li key={item.key} className="flex items-start gap-3 text-sm">
                    {item.done
                      ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 flex-none" />
                      : <Circle className="w-4 h-4 mt-0.5 text-white/30 flex-none" />}
                    <span className={item.done ? 'text-white/80 line-through' : 'text-white/70'}>{item.label}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>

            {/* Production pipeline */}
            {pkg && (
              <GlassCard testid="athlete-me-production" title="Premium Package Pipeline" icon={Film} accent="text-cyan-400"
                subtitle={pkgStatus && (
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${
                    pkgStatus === 'delivered' ? 'bg-emerald-500/20 text-emerald-300' :
                    pkgStatus === 'in_production' ? 'bg-cyan-500/20 text-cyan-300' :
                    pkgStatus === 'paid' ? 'bg-cyan-500/20 text-cyan-300' :
                    'bg-amber-500/20 text-amber-300'}`}>{String(pkgStatus).replace(/_/g,' ')}</span>
                )}
              >
                {/* Pipeline dots */}
                <div className="grid grid-cols-6 gap-1 mb-4">
                  {PRODUCTION_STAGES.map((s, i) => {
                    const done = currentStageIdx > i;
                    const active = currentStageIdx === i;
                    return (
                      <div key={s.id} className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold mb-2 transition ${done ? 'bg-emerald-500 text-black' : active ? 'text-white ring-4' : 'bg-white/10 text-white/40'}`}
                             style={active ? { background: accent, ringColor: `${accent}55` } : {}}>
                          {done ? '✓' : i + 1}
                        </div>
                        <p className={`text-[9px] sm:text-[10px] uppercase tracking-tight text-center leading-tight ${active ? 'font-bold text-white' : 'text-white/40'}`}>{s.label}</p>
                        {active && <p className="hidden sm:block text-[9px] text-white/50 text-center mt-0.5">{s.caption}</p>}
                      </div>
                    );
                  })}
                </div>
                {production.editor && (
                  <p className="text-xs text-white/60"><span className="text-white/40 uppercase tracking-widest text-[10px] font-bold">Editor · </span>{production.editor}</p>
                )}
                {production.deliverables_url && (
                  <a href={production.deliverables_url} target="_blank" rel="noreferrer"
                     className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-cyan-300 hover:text-cyan-200" data-testid="athlete-deliverables-link">
                    <ExternalLink className="w-3.5 h-3.5" /> View deliverables
                  </a>
                )}
              </GlassCard>
            )}

            {/* Upcoming kickoff */}
            {onboarding.kickoff_scheduled_at && (
              <GlassCard testid="athlete-kickoff-card" title="Upcoming Kickoff" icon={Calendar} accent="text-fuchsia-400">
                <p className="text-2xl font-display text-white">{fmtDT(onboarding.kickoff_scheduled_at)}</p>
                <p className="text-xs text-white/50 mt-1">Your kickoff call is on the books. Show up ready with your story ideas.</p>
              </GlassCard>
            )}

            {/* Agent chip */}
            {agent && (
              <GlassCard testid="athlete-agent-card" title="Your Agent" icon={Trophy} accent="text-amber-400">
                <div className="flex items-center gap-4">
                  {agent.agency_logo || agent.photo ? (
                    <img src={resolveSrc(agent.agency_logo || agent.photo)} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20" />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold" style={{ background: agent.accent_color }}>{(agent.agency_name || agent.name || '?').charAt(0)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{agent.agency_name}</p>
                    <p className="text-[11px] text-white/50 truncate">{agent.name}</p>
                  </div>
                  <Link to="/athlete/messages" data-testid="athlete-open-agent-chat"
                          className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold">Message</Link>
                </div>
              </GlassCard>
            )}

            {/* Basics — name, photo, sport, team, position. These now
                sync straight to the public network page (/network/<slug>)
                and every athlete card the moment the athlete saves,
                exactly like editing your profile on any social app. */}
            <AthleteBasicsEditor
              fallbackPhoto={athlete.photo || ''}
              initial={{
                name:               athlete.name || '',
                network_hero_image: athlete.network_hero_image || '',
                network_tagline:    athlete.network_tagline || '',
                sport:              athlete.sport || '',
                team:               athlete.team || '',
                position:           athlete.position || '',
                hometown:           athlete.hometown || '',
                jersey_number:      athlete.jersey_number || '',
                grade_year:         athlete.grade_year || '',
                competition_level:  athlete.competition_level || '',
                business_email:     athlete.business_email || '',
              }}
              onSaved={(patch) => {
                setData((d) => (d ? { ...d, athlete: { ...(d.athlete || {}), ...patch } } : d));
              }}
            />

            {/* iter357 · Payouts are offline, so the "Personal Share Link"
                (fan-signup revenue attribution) card is misleading. We
                replaced it with a "Become a FLYT Ambassador" CTA — the
                actual live growth program with points, badges + tiers. */}
            <GlassCard testid="athlete-me-ambassador" title="Become a FLYT Ambassador" icon={Sparkles} accent="text-cyan-400">
              <p className="text-xs text-white/60 mb-4 leading-relaxed">
                Earn points, badges, and tier upgrades every time an athlete or fan you refer joins Flythouse. Unlock exclusive drops and a shareable Ambassador page.
              </p>
              <Link
                to="/ambassador"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-xs font-bold uppercase tracking-widest hover:bg-cyan-300 transition"
                data-testid="athlete-me-ambassador-cta"
              >
                <Sparkles className="w-3.5 h-3.5" /> Open Ambassador Console
              </Link>
            </GlassCard>

            {/* iter360 · Business inquiries analytics — surfaces how many
                brands / recruiters / fans tapped the athlete's public
                "Business inquiries" pill this month + week + all-time,
                with a "Recent activity" drawer showing the last 10
                events. Hidden when the athlete hasn't set a business
                email yet (soft empty state prompts them to add one). */}
            <BusinessInquiriesTile />

            {/* Athlete-first platform links — Hudl / MaxPreps / Opendorse.
                Athletes can add or update these URLs themselves so
                recruiters, brands and fans can jump straight into
                their game film, verified stats, and NIL availability. */}
            <AthletePlatformsEditor
              initial={{
                hudl_url:      athlete.hudl_url || '',
                maxpreps_url:  athlete.maxpreps_url || '',
                opendorse_url: athlete.opendorse_url || '',
              }}
              onSaved={(patch) => {
                setData((d) => (d ? { ...d, athlete: { ...(d.athlete || {}), ...patch } } : d));
              }}
            />

            {/* iter291 · Founding Athlete direct-contact button —
                only for athletes who claimed a spot. */}
            {athlete.founding_athlete && (
              <FoundingContactButton />
            )}

            {/* iter853 · Jay: agent persona isn't live yet — hide the
                "Send to your agent" growth-loop CTA everywhere until it
                is. The separate "Your Agent" card above (shown only
                when `agent` is already set) is untouched. */}
          </>
        )}

        {/* iter357 · Earnings tab UI removed pre-launch alongside the
            Payouts tab. Both surfaces relied on Stripe Connect payouts
            which are deferred. Re-mount the table block here when the
            earnings ledger comes back. */}

        {/* iter369 · Payouts tab UI removed pre-launch. */}

        {tab === 'notifications' && (
          <div className="space-y-4">
            <GlassCard testid="athlete-me-notifications" title="Nudge History" icon={Bell} accent="text-blue-300"
              subtitle="Every time an agent nudged you — you&apos;ll see who reached out and when.">
              {nudges === null ? (
                <p className="p-6 text-center text-sm text-white/50">Loading…</p>
              ) : nudges.length === 0 ? (
                <p className="p-6 text-center text-sm text-white/50">
                  No nudges yet. When one of your agents pings you (e.g. to finish
                  your Stripe payout setup) it&apos;ll show up here.
                </p>
              ) : (
                <ul className="divide-y divide-white/[0.06]">
                  {nudges.map((n) => (
                    <li key={n.id}
                      className="flex items-center gap-3 px-3 py-3"
                      data-testid={`athlete-nudge-row-${n.id}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-blue-400/15 ring-1 ring-blue-400/40 flex items-center justify-center text-blue-200 shrink-0">
                        <Bell className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">
                          {n.actor} · {n.title}
                        </p>
                        <p className="text-[11px] text-white/50">
                          {fmtDT(n.sent_at)}
                          {n.email_sent && <span className="ml-2 text-white/40">· email sent</span>}
                          {n.push_sent > 0 && <span className="ml-2 text-white/40">· push × {n.push_sent}</span>}
                        </p>
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${
                        n.seen ? 'bg-white/5 text-white/50' : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {n.seen ? 'Seen' : 'New'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </GlassCard>
          </div>
        )}

      </div>
    </div>
  );
};

const GlassCard = ({ title, icon: Icon, accent = 'text-white', subtitle, children, testid }) => (
  <div className="mb-6 rounded-2xl bg-white/[0.04] border border-white/10 p-5 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.4)]" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className={`w-4 h-4 ${accent}`} />}
      <h2 className="text-sm font-bold uppercase tracking-widest text-white">{title}</h2>
      {subtitle && <span className="ml-auto">{subtitle}</span>}
    </div>
    {children}
  </div>
);

const PubStat = ({ label, value, icon: Icon, accent = 'text-white', sub, testid }) => (
  <div className="rounded-2xl bg-white/[0.06] border border-white/10 p-4 backdrop-blur-xl" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</span>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <p className={`font-display text-2xl leading-none ${accent}`}>{value}</p>
    {sub ? <p className="text-[10px] text-white/40 uppercase tracking-wider mt-2">{sub}</p> : null}
  </div>
);


/* ─────────────────────────────────────────────────────────────────
 * AthletePlatformsEditor
 * ─────────────────────────────────────────────────────────────────
 * Self-service card for Hudl / MaxPreps / Opendorse links. Saves via
 * PUT /api/me/athlete/profile with the whitelisted url fields. Shows a
 * per-field "Open" chip once a URL is set so athletes can verify it
 * without leaving the page. */
const PLATFORM_ROWS = [
  {
    key: 'hudl_url', label: 'Hudl', caption: 'Game film',
    icon: Film, color: '#FF6600',
    placeholder: 'https://www.hudl.com/profile/…',
    testid: 'platforms-hudl',
  },
  {
    key: 'maxpreps_url', label: 'MaxPreps', caption: 'Verified prep stats',
    icon: BarChart3, color: '#E31C25',
    placeholder: 'https://www.maxpreps.com/…',
    testid: 'platforms-maxpreps',
  },
  {
    key: 'opendorse_url', label: 'Opendorse', caption: 'NIL deals',
    icon: Handshake, color: '#00A9E0',
    placeholder: 'https://opendorse.com/…',
    testid: 'platforms-opendorse',
  },
];

const normalizeUrl = (v) => {
  const s = (v || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};

export const AthletePlatformsEditor = ({ initial, onSaved }) => {
  const [values, setValues] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  const cardRef = useRef(null);

  // Auto-scroll + brief highlight when the athlete opens the page via
  // `/athlete/me#platforms` (the shortcut lives on the Career page).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#platforms') return;
    const el = cardRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-cyan-400/70');
      setTimeout(() => el.classList.remove('ring-2', 'ring-cyan-400/70'), 2400);
    }, 250);
    return () => clearTimeout(t);
  }, []);

  const dirty = useMemo(() => {
    return PLATFORM_ROWS.some((r) => (values[r.key] || '') !== (initial?.[r.key] || ''));
  }, [values, initial]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        updates: PLATFORM_ROWS.reduce((acc, r) => {
          const raw = (values[r.key] || '').trim();
          acc[r.key] = raw ? normalizeUrl(raw) : null;
          return acc;
        }, {}),
      };
      const r = await axios.put(`${API}/me/athlete/profile`, payload, { withCredentials: true });
      const patch = {
        hudl_url: r.data?.hudl_url || null,
        maxpreps_url: r.data?.maxpreps_url || null,
        opendorse_url: r.data?.opendorse_url || null,
      };
      setValues({
        hudl_url:      patch.hudl_url || '',
        maxpreps_url:  patch.maxpreps_url || '',
        opendorse_url: patch.opendorse_url || '',
      });
      onSaved?.(patch);
      toast.success('Platform links saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={cardRef} id="platforms" className="scroll-mt-24 rounded-2xl transition-shadow duration-500">
    <GlassCard
      testid="athlete-me-platforms"
      title="Athlete Platforms"
      icon={ExternalLink}
      accent="text-cyan-400"
      subtitle={dirty && (
        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Unsaved</span>
      )}
    >
      <p className="text-xs text-white/60 mb-4">
        Add your Hudl film, MaxPreps stats, and Opendorse NIL page. These appear as branded buttons on your public profile so recruiters and brands can jump straight in.
      </p>
      <div className="space-y-3">
        {PLATFORM_ROWS.map(({ key, label, caption, icon: Icon, color, placeholder, testid }) => {
          const current = values[key] || '';
          const savedUrl = initial?.[key] || '';
          return (
            <div key={key} className="rounded-xl bg-black/30 border border-white/10 p-3" data-testid={testid}>
              <div className="flex items-center gap-3 mb-2">
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                  style={{ background: `${color}22`, color }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white leading-tight">{label}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">{caption}</p>
                </div>
                {savedUrl && (
                  <a
                    href={savedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-cyan-300 hover:text-cyan-200"
                    data-testid={`${testid}-open`}
                  >
                    <ExternalLink className="w-3 h-3" /> Open
                  </a>
                )}
              </div>
              <input
                type="url"
                value={current}
                onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                placeholder={placeholder}
                className="w-full text-xs font-mono rounded-lg focus:outline-none"
                style={{
                  // Beat the global App.css white-input rule so the typed
                  // URL is readable on the dark Liquid Glass surface.
                  color: '#ffffff',
                  caretColor: '#ffffff',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  borderRadius: 10,
                  padding: '11px 13px',
                  fontSize: 16, // prevent iOS auto-zoom on focus
                }}
                data-testid={`${testid}-input`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        {dirty && (
          <button
            onClick={() => setValues({
              hudl_url:      initial?.hudl_url || '',
              maxpreps_url:  initial?.maxpreps_url || '',
              opendorse_url: initial?.opendorse_url || '',
            })}
            className="text-[11px] uppercase tracking-widest font-bold text-white/50 hover:text-white/80 px-3 py-2"
            data-testid="platforms-reset"
          >
            Reset
          </button>
        )}
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-white text-black hover:bg-cyan-300 transition disabled:opacity-40 disabled:hover:bg-white"
          data-testid="platforms-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : 'Save platforms'}
        </button>
      </div>
    </GlassCard>
    </div>
  );
};

export default AthleteMePage;

// ─── Basics editor — name, photo, sport, team, tagline ──────────────
// Writes to the same whitelisted PUT /me/athlete/profile endpoint used
// everywhere else. On save the backend flushes the network cache so
// the public /network/<slug> reflects the change instantly.
export const AthleteBasicsEditor = ({ initial, onSaved, alwaysExpanded = false, fallbackPhoto = '' }) => {
  const [values, setValues] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  // When `alwaysExpanded` is true (modal mode) the collapsed summary
  // row is skipped entirely — athletes go straight to the full form.
  const [open, setOpen] = useState(alwaysExpanded);
  // Which photo field is currently uploading? null | 'photo' | 'network_hero_image'
  const [uploadingKey, setUploadingKey] = useState(null);
  const photoInputRef = useRef(null);
  const heroInputRef  = useRef(null);
  // iter454.17 · Unique per-instance id so the <label htmlFor=…>
  // association still resolves correctly when this component is
  // rendered twice on the same page (e.g., inline on /athlete/me AND
  // inside the BasicsEditorModal on /network/{slug}). Duplicate ids
  // would let the modal's label point at the inline input, and the
  // native picker would open behind the modal — invisible to the
  // athlete.
  const photoInputId = useId();
  const dirty = useMemo(
    () => Object.keys(initial || {}).some((k) => (values[k] || '') !== (initial?.[k] || '')),
    [values, initial],
  );
  const save = async () => {
    setSaving(true);
    try {
      // iter455 · DIRTY-ONLY payload. The modal can mount from a stale
      // SWR-cached athlete; serializing the whole form used to re-send
      // that stale network_hero_image and roll back a photo the athlete
      // had just uploaded. Only fields the athlete actually changed in
      // THIS session go over the wire.
      const payload = {};
      for (const [k, v] of Object.entries(values)) {
        const next = (v || '').toString().trim();
        const prev = (initial?.[k] || '').toString().trim();
        if (next !== prev) payload[k] = next || null;
      }
      if (!Object.keys(payload).length) {
        toast.success('Nothing to save — no changes made.');
        setSaving(false);
        return;
      }
      const r = await axios.put(`${API}/me/athlete/profile`, { updates: payload }, { withCredentials: true });
      const patch = Object.fromEntries(
        Object.keys(initial || {}).map((k) => [k, r.data?.[k] ?? null])
      );
      onSaved?.(patch);
      // iter · Broadcast the change so any mounted surface (Launch Pad
      // home island, avatar) updates INSTANTLY without waiting on a
      // refetch or the CDN cache.
      try { window.dispatchEvent(new CustomEvent('flyt:profile-updated', { detail: patch })); } catch { /* noop */ }
      toast.success('Saved — live on your network now.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // iter454.18 · Client-side image compression before upload. Native
  // iOS Photos exports HEIC or 12MP JPEGs that hit 5–10MB per shot —
  // uploading raw bytes over cellular takes 10–20s. We downscale to
  // 1200px max edge + JPEG q=0.85 on a canvas, which produces a
  // ~200–400KB file with visually identical quality on the network
  // hero. Result: upload drops from 15s → sub-second on typical LTE.
  // HEIC handled by the browser's built-in decoder via <img src=blob>
  // — Safari/WKWebView both natively decode HEIC to canvas.
  const compressImage = (file) => new Promise((resolve) => {
    // Videos or files already tiny — skip the canvas round-trip.
    if (!file.type.startsWith('image/') || file.size < 500 * 1024) {
      resolve(file);
      return;
    }
    const MAX_EDGE = 1200;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width: w, height: h } = img;
        if (w > MAX_EDGE || h > MAX_EDGE) {
          const scale = MAX_EDGE / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) { resolve(file); return; }
            // Wrap the blob in a File so multipart uploaders still get
            // a real filename + type. Force .jpg since we're re-
            // encoding as JPEG (HEIC → JPEG conversion happens here).
            const compressed = new File([blob], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressed.size < file.size ? compressed : file);
          },
          'image/jpeg',
          0.85
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        console.warn('compressImage failed, uploading original', err);
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // HEIC that the browser can't decode — upload raw, backend
      // handles conversion via Cloudinary.
      resolve(file);
    };
    img.src = url;
  });

  // Tap-to-upload: two-stage flow — first push the file bytes to the
  // CDN via /me/upload-photo (Cloudinary primary, Emergent fallback),
  // then persist the returned URL onto the athlete row via
  // PUT /me/athlete/profile so the network page + every roster tile
  // reflects it instantly. On upload failure we fall back to the
  // proven /me/upload-avatar pipeline (same Cloudinary route, does not
  // require a linked-athlete lookup) so a transient issue on one
  // endpoint never blocks the athlete from changing their photo.
  const uploadPhoto = async (rawFile, fieldKey) => {
    if (!rawFile) return;
    if (!rawFile.type?.startsWith('image/')) {
      toast.error('That file is not an image.');
      return;
    }
    if (rawFile.size > 25 * 1024 * 1024) {
      toast.error('Image is too large (max 25 MB).');
      return;
    }
    setUploadingKey(fieldKey);

    // iter454.18 · Compress FIRST, then upload. This is the single
    // biggest lever for the "under 2 seconds" requirement — a 6MB
    // iPhone photo becomes ~250KB, which uploads in a few hundred ms
    // even on 3G.
    const file = await compressImage(rawFile);

    const uploadOnce = async (endpoint) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}${endpoint}`, fd, {
        withCredentials: true,
        timeout: 30_000,
      });
      return res.data?.url || '';
    };

    let raw = '';
    try {
      raw = await uploadOnce('/me/upload-photo');
    } catch (primaryErr) {
      // Log the primary failure with as much context as possible so the
      // "Upload failed" fallback is never opaque during debugging.
      console.error('upload-photo failed, trying upload-image fallback', {
        status: primaryErr?.response?.status,
        detail: primaryErr?.response?.data?.detail,
        code: primaryErr?.code,
        message: primaryErr?.message,
      });
      try {
        // iter455 · Fallback is the side-effect-free generic uploader.
        // The previous fallback (/me/upload-avatar) silently overwrote
        // public_users.picture — the GLOBAL avatar — which is exactly
        // the cross-surface bleed Jay reported. /me/upload-image just
        // returns a CDN URL and touches nothing.
        raw = await uploadOnce('/me/upload-image');
      } catch (fallbackErr) {
        console.error('upload-image fallback also failed', {
          status: fallbackErr?.response?.status,
          detail: fallbackErr?.response?.data?.detail,
          code: fallbackErr?.code,
          message: fallbackErr?.message,
        });
        const primaryDetail = primaryErr?.response?.data?.detail;
        const fallbackDetail = fallbackErr?.response?.data?.detail;
        const timedOut = primaryErr?.code === 'ECONNABORTED' || fallbackErr?.code === 'ECONNABORTED';
        toast.error(
          fallbackDetail || primaryDetail ||
          (timedOut ? 'Upload timed out — check your connection and try again.' : 'Upload failed — try again.')
        );
        setUploadingKey(null);
        return;
      }
    }

    if (!raw) {
      toast.error('Upload finished but no image URL returned. Try again.');
      setUploadingKey(null);
      return;
    }

    try {
      // iter454.18 · Cache-bust ONLY the display URL. The stored URL
      // stays clean in Mongo (raw Cloudinary URL, no query params), so
      // future loads don't accumulate stale ?v= timestamps. The bust
      // is only applied to what the current UI renders — that's what
      // forces the img element to fetch a fresh bitmap right now.
      const bust = (u) => u ? u + (u.includes('?') ? '&' : '?') + 'v=' + Date.now() : u;

      // Persist immediately so the network reflects it — no separate Save
      // click needed for the "tap the photo → done" flow. iter455 ·
      // DIRTY-ONLY: send the new image field plus only the text fields
      // the athlete changed in this session. Serializing the whole form
      // used to push stale cached values (e.g. an old network_hero_image
      // from SWR cache) and clobber fresher server state.
      const nextValues = { ...values, [fieldKey]: raw };
      setValues(nextValues);
      const payload = { [fieldKey]: raw };
      for (const [k, v] of Object.entries(nextValues)) {
        if (k === fieldKey) continue;
        const next = (v || '').toString().trim();
        const prev = (initial?.[k] || '').toString().trim();
        if (next !== prev) payload[k] = next || null;
      }
      const r = await axios.put(`${API}/me/athlete/profile`, { updates: payload }, { withCredentials: true });
      const patch = Object.fromEntries(
        Object.keys(initial || {}).map((k) => [k, r.data?.[k] ?? null])
      );
      // Cache-bust the display URL that flows to the parent so the
      // network hero + roster tiles re-fetch the new image immediately
      // instead of showing WKWebView's cached copy of the previous URL.
      if (patch[fieldKey]) patch[fieldKey] = bust(patch[fieldKey]);
      onSaved?.(patch);
      // iter · Push the new photo to the Launch Pad / avatar instantly.
      try { window.dispatchEvent(new CustomEvent('flyt:profile-updated', { detail: patch })); } catch { /* noop */ }
      toast.success('Photo updated — live now');
    } catch (e) {
      console.error('athlete/profile save after upload failed', {
        status: e?.response?.status,
        detail: e?.response?.data?.detail,
        code: e?.code,
        message: e?.message,
      });
      toast.error(e?.response?.data?.detail || 'Photo uploaded but could not be saved. Try again.');
    } finally {
      setUploadingKey(null);
    }
  };

  const heroSrc = values.network_hero_image || fallbackPhoto;
  return (
    <GlassCard
      testid="athlete-me-basics"
      title="Profile Basics"
      icon={User}
      accent="text-blue-300"
      subtitle={dirty ? (
        <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Unsaved</span>
      ) : null}
    >
      <p className="text-xs text-white/60 mb-3">
        Your name and details here sync across the network. The photo here is your <span className="text-white/85 font-bold">Network profile photo</span> — it only changes your network page. To change your photo everywhere, update it on your Player Profile Card.
      </p>
      {/* Summary row (avatar + name + Edit/Close toggle) — always
          rendered, even in modal mode. In modal mode `open` starts
          true so the button reads "Close" and the form is already
          visible below — but we keep the row for context. */}
      <div className="flex items-center gap-3 mb-3">
        {/* iter454.17 · iOS-Capacitor safe photo picker. Previously we
            used <button onClick={ref.click()}> to trigger a hidden
            <input type=file>. On native iOS inside a position:fixed
            modal, WKWebView sometimes swallows programmatic .click()
            on the input (the picker just no-ops silently). Switching
            to a <label htmlFor> pattern means the tap directly opens
            the native Photos picker — no synthetic click required —
            which is Apple's blessed path and identical to how every
            iOS-native web app (Twitter, Instagram web) does it.
            Wrapping the label makes the whole 64px circle tappable. */}
        <label
          htmlFor={photoInputId}
          className={`relative w-16 h-16 rounded-full overflow-hidden bg-white/10 ring-1 ring-white/10 hover:ring-white/40 flex items-center justify-center shrink-0 transition group focus-within:ring-2 focus-within:ring-cyan-300 ${uploadingKey === 'network_hero_image' ? 'pointer-events-none opacity-80' : 'cursor-pointer'}`}
          data-testid="basics-photo-tap"
          aria-label="Change network profile photo"
          title="Tap to upload your network profile photo"
        >
          {heroSrc
            ? <img src={heroSrc} alt="" className="w-full h-full object-cover" />
            : <User className="w-6 h-6 text-white/40" />}
          <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
            {uploadingKey === 'network_hero_image'
              ? <Loader2 className="w-5 h-5 text-white animate-spin" />
              : <Camera className="w-5 h-5 text-white" />}
          </span>
          {uploadingKey === 'network_hero_image' && (
            <span className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </span>
          )}
        </label>
        <input
          ref={photoInputRef}
          id={photoInputId}
          type="file"
          accept="image/*"
          onChange={(e) => uploadPhoto(e.target.files?.[0], 'network_hero_image')}
          data-testid="basics-photo-file"
          disabled={uploadingKey === 'network_hero_image'}
          // iter454.17 · sr-only (positioned off-screen) instead of
          // `display:none`. WKWebView refuses to fire `.click()` on
          // inputs with `display:none` in some builds; sr-only keeps
          // the element in the layout so the label association works
          // reliably even in the position:fixed modal container.
          className="sr-only"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">{values.name || 'Add your name'}</p>
          <p className="text-[11px] text-white/50 truncate">
            {values.sport || 'Sport'} · {values.team || 'Team'} {values.position ? `· ${values.position}` : ''}
          </p>
        </div>
        {/* Hide the toggle when the parent (modal) already provides a
            close affordance — otherwise athletes get two "Close"s. */}
        {alwaysExpanded ? null : (
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-[10px] uppercase tracking-widest font-bold text-white/70 hover:text-white px-3 py-1.5 rounded-full ring-1 ring-white/10 hover:bg-white/10 transition"
            data-testid="basics-toggle"
          >
            {open ? 'Close' : 'Edit'}
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-3 pt-3 border-t border-white/10">
          {/* Tap-to-upload tiles for the two photo fields — replace the
              old raw-URL inputs so athletes never have to paste a link.
              Each tile is a big tappable target: click → file picker →
              /api/upload → auto-PUT → live on the network. */}
          {/* iter451 · Removed the standalone Profile Photo + Hero Photo
              tiles per Jay's direction. The tap-to-upload avatar circle
              at the top of this card is now the SINGLE source of truth
              for every profile image on the network — profile card, FLYT
              ID header, network circles, /network/{slug} hero — they all
              mirror whatever the athlete drops into that one circle
              (see PUT /me/athlete/profile · photo-mirroring block).
              This kills the confusion of 5 different pictures showing
              up in 5 different places. */}
          {[
            { key: 'name',               label: 'Display name',    placeholder: 'e.g. Kelvion Riggins' },
            { key: 'network_tagline',    label: 'One-line tagline',placeholder: 'e.g. Two-way DB. Kicks fast.' },
            { key: 'sport',              label: 'Sport',            placeholder: 'Football, Basketball, …' },
            { key: 'team',               label: 'Team / School',    placeholder: 'e.g. Texas Longhorns' },
            { key: 'position',           label: 'Position',         placeholder: 'e.g. RB' },
            { key: 'jersey_number',      label: 'Jersey number',    placeholder: 'e.g. 5' },
            { key: 'hometown',           label: 'Hometown',         placeholder: 'e.g. Fort Worth, TX' },
            { key: 'grade_year',         label: 'Class of',         placeholder: 'e.g. 2026' },
            // iter359 · Business inquiries — surfaces as a small blue
            // pill at the top of the athlete's public /network/{slug}
            // and /l/{slug} pages, opening a pre-filled mailto so
            // brands / recruiters / media can reach the athlete (or
            // their manager / agent) in one tap. Optional — leave
            // blank to hide the pill entirely.
            { key: 'business_email',     label: 'Business inquiries email', placeholder: 'e.g. booking@yourname.com', type: 'email', hint: 'Shows a blue "Business inquiries" pill on your profile. Leave blank to hide.' },
          ].map(({ key, label, placeholder, type, hint }) => (
            <label key={key} className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</span>
              <input
                type={type || 'text'}
                value={values[key] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                placeholder={placeholder}
                className="mt-1 w-full text-sm rounded-lg focus:outline-none"
                style={{
                  color: '#ffffff', caretColor: '#ffffff',
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  padding: '8px 12px',
                }}
                data-testid={`basics-input-${key}`}
              />
              {hint && (
                <p className="mt-1 text-[10px] text-white/40 leading-snug">{hint}</p>
              )}
            </label>
          ))}
          {/* iter196 · Competition level — segmented picker (NFL /
              College / Pro / HS) with a custom text override. Shows
              as a pill on the agent's FLYT ID™ roster row + on the
              athlete's public network page. */}
          <div className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Competition level</span>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              {['NFL', 'College', 'Pro', 'HS'].map((opt) => {
                const active = (values.competition_level || '').toLowerCase() === opt.toLowerCase();
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setValues((v) => ({ ...v, competition_level: opt }))}
                    className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold transition ${active ? 'bg-yellow-400 text-black' : 'bg-white/[0.05] text-white/70 hover:bg-white/[0.12] ring-1 ring-white/10'}`}
                    data-testid={`basics-level-${opt.toLowerCase()}`}
                  >
                    {opt}
                  </button>
                );
              })}
              <input
                type="text"
                value={values.competition_level && !['NFL', 'College', 'Pro', 'HS'].some((o) => o.toLowerCase() === (values.competition_level || '').toLowerCase()) ? values.competition_level : ''}
                onChange={(e) => setValues((v) => ({ ...v, competition_level: e.target.value }))}
                placeholder="Or type your own"
                className="flex-1 min-w-[140px] text-[12px] rounded-full focus:outline-none"
                style={{
                  color: '#ffffff', caretColor: '#ffffff',
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  padding: '6px 12px',
                }}
                data-testid="basics-input-competition_level"
              />
            </div>
            <p className="text-[10px] text-white/40 mt-1">Shows as a pill on the agent&rsquo;s FLYT ID™ roster row and your public network profile.</p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            {dirty && (
              <button
                onClick={() => setValues(initial || {})}
                className="text-[11px] uppercase tracking-widest font-bold text-white/50 hover:text-white/80 px-3 py-2"
                data-testid="basics-reset"
              >
                Reset
              </button>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-white text-black hover:bg-blue-300 transition disabled:opacity-40 disabled:hover:bg-white"
              data-testid="basics-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {saving ? 'Saving…' : 'Save & push to network'}
            </button>
          </div>
        </div>
      )}
    </GlassCard>
  );
};

/**
 * PhotoUploadTile — square tap-to-upload target rendered inside the
 * Basics editor. Shows the current image or a "tap to upload" empty
 * state; delegates the actual file picker + upload to the parent so
 * every tile funnels through the shared `uploadPhoto()` helper (which
 * auto-PUTs via /me/athlete/profile so the network stays in sync).
 */
const PhotoUploadTile = ({ label, value, uploading, onTap, onClear, testid }) => {
  const src = value ? (value.startsWith('http') ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : '';
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</span>
      <button
        type="button"
        onClick={onTap}
        disabled={uploading}
        className="mt-1 relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-black/60 ring-1 ring-white/10 hover:ring-white/40 transition group focus:outline-none focus:ring-2 focus:ring-cyan-300 disabled:opacity-70"
        data-testid={testid}
        aria-label={`Change ${label.toLowerCase()}`}
      >
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60">
            <Camera className="w-6 h-6 mb-1" />
            <span className="text-[10px] uppercase tracking-widest font-bold">Tap to upload</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
        {src && !uploading && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-bold text-white">Replace</span>
          </div>
        )}
      </button>
      {src && !uploading && (
        <button
          type="button"
          onClick={onClear}
          className="mt-1 text-[10px] uppercase tracking-widest font-bold text-white/40 hover:text-red-300 transition"
          data-testid={`${testid}-clear`}
        >
          Remove
        </button>
      )}
    </label>
  );
};
